import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  Assignment,
  CheckCircle,
  Schedule,
  Warning,
  Send,
  Visibility
} from '@mui/icons-material';
import { trackingService } from '../services/trackingService';
import { useNotification } from '../contexts/NotificationContext';

const Tracking = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const response = await trackingService.getApplications();
      setApplications(response.applications || []);
    } catch (error) {
      console.error('Failed to load applications:', error);
      showError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitApplication = async (applicationId) => {
    try {
      setSubmitting(applicationId);
      const response = await trackingService.submitApplication(applicationId);
      showSuccess(`Application submitted successfully! Tracking number: ${response.trackingNumber}`);
      loadApplications(); // Reload to get updated status
    } catch (error) {
      console.error('Failed to submit application:', error);
      showError('Failed to submit application');
    } finally {
      setSubmitting(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'submitted': return 'primary';
      case 'under_review': return 'info';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return <Assignment />;
      case 'submitted': return <Send />;
      case 'under_review': return <Schedule />;
      case 'approved': return <CheckCircle />;
      case 'rejected': return <Warning />;
      default: return <Assignment />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const renderTimeline = (progress) => {
    const timelineEvents = [
      { label: 'Application Started', completed: true },
      { label: 'Profile Completed', completed: true },
      { label: 'Form Submitted', completed: progress.percentage >= 30 },
      { label: 'Under Review', completed: progress.percentage >= 50 },
      { label: 'Decision Made', completed: progress.percentage >= 100 }
    ];

    return (
      <Timeline>
        {timelineEvents.map((event, index) => (
          <TimelineItem key={index}>
            <TimelineSeparator>
              <TimelineDot 
                color={event.completed ? 'primary' : 'grey'}
                variant={event.completed ? 'filled' : 'outlined'}
              >
                {event.completed ? <CheckCircle /> : <Schedule />}
              </TimelineDot>
              {index < timelineEvents.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <Typography 
                variant="body2" 
                color={event.completed ? 'text.primary' : 'text.secondary'}
              >
                {event.label}
              </Typography>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
              Application Tracking
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Monitor your immigration application progress
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
          >
            New Application
          </Button>
        </Box>

        {applications.length === 0 ? (
          <Alert severity="info">
            <Typography variant="body1" sx={{ mb: 2 }}>
              You haven't started any applications yet.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/dashboard')}
              startIcon={<Assignment />}
            >
              Start New Application
            </Button>
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Applications List */}
            <Grid item xs={12} md={selectedApp ? 6 : 12}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Your Applications
              </Typography>
              
              {applications.map((app) => (
                <Card 
                  key={app.id} 
                  sx={{ 
                    mb: 2, 
                    cursor: 'pointer',
                    border: selectedApp?.id === app.id ? 2 : 1,
                    borderColor: selectedApp?.id === app.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => setSelectedApp(app)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h3">
                          {app.country} - {app.visa_type}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Created: {formatDate(app.created_at)}
                        </Typography>
                      </Box>
                      <Chip
                        icon={getStatusIcon(app.status)}
                        label={app.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(app.status)}
                        size="small"
                      />
                    </Box>

                    {/* Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          Progress
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {app.progress?.percentage || 0}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={app.progress?.percentage || 0}
                        sx={{ height: 6, borderRadius: 3 }}
                      />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {app.status === 'draft' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/forms/${app.country}/${app.visa_type}`);
                            }}
                          >
                            Continue Editing
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSubmitApplication(app.id);
                            }}
                            disabled={submitting === app.id}
                            startIcon={submitting === app.id ? <CircularProgress size={16} /> : <Send />}
                          >
                            {submitting === app.id ? 'Submitting...' : 'Submit'}
                          </Button>
                        </>
                      )}
                      
                      {app.status !== 'draft' && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedApp(app);
                          }}
                        >
                          View Details
                        </Button>
                      )}
                    </Box>

                    {app.estimatedCompletion && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Estimated completion: {formatDate(app.estimatedCompletion)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Grid>

            {/* Application Details */}
            {selectedApp && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                      Application Details
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Application Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Country: {selectedApp.country}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visa Type: {selectedApp.visa_type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {selectedApp.status.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created: {formatDate(selectedApp.created_at)}
                      </Typography>
                      {selectedApp.submitted_at && (
                        <Typography variant="body2" color="text.secondary">
                          Submitted: {formatDate(selectedApp.submitted_at)}
                        </Typography>
                      )}
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Stage
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        {selectedApp.progress?.stage || 'Application Draft'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={selectedApp.progress?.percentage || 0}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Application Timeline
                      </Typography>
                      {renderTimeline(selectedApp.progress || { percentage: 0 })}
                    </Box>

                    {selectedApp.estimatedCompletion && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          Estimated completion: {formatDate(selectedApp.estimatedCompletion)}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default Tracking;